// Pure model of the schema-driven forms (create wizard, revise, duplicate):
// value paths, initial values and prefills, validation and the mapping of
// server errors. No React; the form schema comes from GET /types/. The
// validation mirrors the backend's registry/form_validation.py, so a form
// that passes here is accepted by the server (apart from its layout checks,
// which measure texts with the como fonts).
import {
  DocumentCraftingCountLimit,
  DocumentCraftingFileKind,
  DocumentCraftingFormField,
  DocumentCraftingFormValues,
  DocumentCraftingPrefill,
  DocumentCraftingRequiredIf,
  DocumentCraftingUpload,
  DocumentCraftingUploadKind,
} from "../../../Models/documentCrafting";

export type FormValues = DocumentCraftingFormValues;

/** Wizard steps; i18n `create.wizard.steps.<key>`. Schema `step: "files"` is
 * the "Sources & files" step. */
export const WIZARD_STEPS = ["basics", "files", "details", "review"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];
export type FieldStep = Exclude<WizardStep, "review">;

/** An error of one field: an i18n key of `create.errors` with params, or a
 * message from the server. */
export interface FormError {
  key?: string;
  params?: Record<string, unknown>;
  message?: string;
}
export type FormErrors = Record<string, FormError>;

export interface LocalImageInfo {
  width_px: number;
  height_px: number;
  /** null when the browser could not read the pixels. */
  has_alpha: boolean | null;
  mean_luminance: number | null;
}

/** A file picked in the form and not uploaded yet (sent with the request). */
export interface PendingFile {
  id: string;
  file: File;
  /** Dimensions of images, read after the drop; undefined while reading. */
  image?: LocalImageInfo | null;
  /** Alt text the user gave the picture (empty: the AI proposes one). */
  alt?: string;
}
export type PendingFiles = Record<string, PendingFile[]>;

/** Total of new files one request may carry (DOCUMENT_CRAFTING_MAX_FILES_PER_RUN). */
export const MAX_FILES_PER_RUN = 20;

const err = (key: string, params?: Record<string, unknown>): FormError => ({
  key,
  params,
});

// Paths: dot separated, list items by index ("packages.0.price").

export const joinPath = (prefix: string, key: string | number) =>
  prefix ? `${prefix}.${key}` : String(key);

const segments = (path: string) => (path ? path.split(".") : []);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const asRecord = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

export const getAt = (values: unknown, path: string): unknown =>
  segments(path).reduce<unknown>((node, segment) => {
    if (Array.isArray(node)) return node[Number(segment)];
    if (isRecord(node)) return node[segment];
    return undefined;
  }, values);

const setIn = (node: unknown, path: string[], value: unknown): unknown => {
  if (!path.length) return value;
  const [head, ...rest] = path;
  if (Array.isArray(node)) {
    const copy = node.slice();
    copy[Number(head)] = setIn(copy[Number(head)], rest, value);
    return copy;
  }
  const record = asRecord(node);
  return { ...record, [head]: setIn(record[head], rest, value) };
};

/** Immutable update of the value at `path`. */
export const setAt = (
  values: FormValues,
  path: string,
  value: unknown,
): FormValues => setIn(values, segments(path), value) as FormValues;

/** Drops the errors of `path` and of everything below it. */
export const withoutErrors = (errors: FormErrors, path: string): FormErrors => {
  const next: FormErrors = {};
  Object.keys(errors).forEach((key) => {
    if (key !== path && !key.startsWith(`${path}.`)) next[key] = errors[key];
  });
  return next;
};

/** DOM id of a field's wrapper, used to focus `?field=` and server errors. */
export const fieldDomId = (path: string) =>
  `dc-field-${path.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

// Emptiness and conditions

/** Deeply empty: null, blank text, NaN, an unticked box, and lists/objects
 * of empty values (the server's `_blank`). */
export const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === "string") return !value.trim();
  if (typeof value === "number") return Number.isNaN(value);
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (isRecord(value)) return Object.keys(value).every((key) => isEmptyValue(value[key]));
  return false;
};

const sameValue = (a: unknown, b: unknown) =>
  (a ?? null) === (b ?? null) || JSON.stringify(a) === JSON.stringify(b);

/** The schema field at a dotted path without list indices ("contact.phone",
 * "packages.price"), like the registry's `DocumentTypeDefinition.field`. */
export const fieldAtPath = (fields: DocumentCraftingFormField[], path: string): DocumentCraftingFormField | null => {
  let current: DocumentCraftingFormField[] = fields;
  let found: DocumentCraftingFormField | null = null;
  const parts = segments(path);
  for (let index = 0; index < parts.length; index += 1) {
    found = current.find((field) => field.key === parts[index]) ?? null;
    if (!found) return null;
    current = found.fields ?? found.item_fields ?? [];
  }
  return found;
};

/**
 * Whether nothing was entered in a field. Selects and checkboxes that still
 * hold their default count as empty, so an untouched optional group (e.g.
 * price conditions with the default "net") is not validated; list rows whose
 * fields are all empty do not count as entries.
 */
export const isBlankField = (
  field: DocumentCraftingFormField,
  value: unknown,
  path: string,
  files: PendingFiles,
): boolean => {
  switch (field.type) {
    case "group": {
      const group = asRecord(value);
      return (field.fields ?? []).every((child) =>
        isBlankField(child, group[child.key], joinPath(path, child.key), files),
      );
    }
    case "list": {
      const items = Array.isArray(value) ? value : [];
      return items.every((item, index) => isBlankRow(field, item, joinPath(path, index), files));
    }
    case "file":
      return isEmptyValue(value) && !(files[path]?.length ?? 0);
    case "select":
    case "boolean":
      return (
        value === null ||
        value === undefined ||
        value === "" ||
        value === false ||
        sameValue(value, field.default)
      );
    default:
      return isEmptyValue(value);
  }
};

/** A list row nobody filled in (the server drops it). */
export const isBlankRow = (
  list: DocumentCraftingFormField,
  item: unknown,
  itemPath: string,
  files: PendingFiles,
): boolean => {
  if (!list.item_fields?.length) return isEmptyValue(item);
  const record = asRecord(item);
  return list.item_fields.every((child) =>
    isBlankField(child, record[child.key], joinPath(itemPath, child.key), files),
  );
};

export interface ValidationContext {
  root: FormValues;
  files: PendingFiles;
  /** The whole schema of the type: resolves the targets of `required_if`
   * (their type and default) when only some fields are validated. */
  fields?: DocumentCraftingFormField[];
  /** Earlier uploads per file field path (duplicate, revise): the ticked
   * ones are checked against `min_width_px` too. */
  uploads?: Record<string, DocumentCraftingUpload[]>;
}

const conditionHolds = (
  condition: DocumentCraftingRequiredIf,
  scope: Record<string, unknown>,
  context: ValidationContext,
): boolean => {
  const target = context.fields ? fieldAtPath(context.fields, condition.field) : null;
  let value = getAt(context.root, condition.field);
  // The registry sends absolute paths; a plain key may still name a sibling.
  if (value === undefined && !condition.field.includes(".") && condition.field in scope) {
    value = scope[condition.field];
  }
  if ((value === undefined || value === null) && target && target.default !== undefined) {
    value = target.default;
  }
  switch (condition.condition) {
    case "not_empty":
      return target
        ? !isBlankField(target, value, condition.field, context.files)
        : !isEmptyValue(value);
    case "equals":
      if (typeof condition.value === "boolean") return (value === true) === condition.value;
      return sameValue(value, condition.value);
    case "contains":
      return Array.isArray(value) && value.some((item) => sameValue(item, condition.value));
    default:
      return false;
  }
};

/** `required`, or `required_if` whose condition holds. A detail with
 * `ask_if_missing` is never required in the form: the analysis takes it from
 * the description or asks for it (the server defers it the same way). */
export const isFieldRequired = (
  field: DocumentCraftingFormField,
  scope: Record<string, unknown>,
  context: ValidationContext,
) =>
  !field.ask_if_missing &&
  (!!field.required || (!!field.required_if && conditionHolds(field.required_if, scope, context)));

// Formats (as registry/form_validation.py checks them)

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
const PHONE_PATTERN = /^[+0-9 ()/.\-–\u00a0]+$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
const NUMBER_CELL_PATTERN = /^[-+−]?\d+(?:[.,]\d+)*$/;
const TABLE_RULE_PATTERN = /^\|?\s*:?-{3,}/;

/** The web address the server stores: http(s) with a host that has a dot;
 * without a scheme "https://" is added. Null when it is none. */
export const normalizeUrl = (text: string): string | null => {
  const value = text.trim();
  if (!value) return null;
  const candidate = SCHEME_PATTERN.test(value) ? value : `https://${value}`;
  if (/\s/.test(candidate)) return null;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.hostname.includes(".") ? candidate : null;
  } catch {
    return null;
  }
};

export const isValidUrl = (value: string) => normalizeUrl(value) !== null;

export const isValidEmail = (value: string) => EMAIL_PATTERN.test(value.trim());

const isUrlOrEmail = (text: string) => {
  const address = text.toLowerCase().startsWith("mailto:") ? text.slice(7) : text;
  if (address.includes("@") && !address.includes("/")) return EMAIL_PATTERN.test(address);
  return isValidUrl(text);
};

export const isValidPhone = (text: string) =>
  PHONE_PATTERN.test(text) && (text.match(/\d/g) ?? []).length >= 5;

const isValidDate = (value: string) => {
  const match = DATE_PATTERN.exec(value.trim());
  if (!match) return false;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.getMonth() === Number(match[2]) - 1 && date.getDate() === Number(match[3]);
};

const isValidTime = (value: string) => {
  const match = TIME_PATTERN.exec(value.trim());
  return !!match && Number(match[1]) <= 23 && Number(match[2]) <= 59;
};

/** Words as the server counts them (`len(text.split())`). */
export const wordCount = (text: string) => text.split(/\s+/).filter(Boolean).length;

/** Text as the server measures it: line breaks normalised, trimmed, and
 * single-line fields joined into one line. */
export const normalizeText = (field: Pick<DocumentCraftingFormField, "type">, value: string) => {
  const text = value.replace(/\r\n?/g, "\n").normalize("NFC").trim();
  return field.type === "textarea" ? text : text.replace(/[ \t]*\n\s*/g, " ");
};

/**
 * Why a pasted chart table cannot be used, or null. Shape: a header row
 * (label, then series names), then one row per category (name, then one
 * number per series); cells separated by tabs, semicolons or pipes.
 */
export const chartTableProblem = (text: string): FormError | null => {
  const rows = text
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row && !TABLE_RULE_PATTERN.test(row));
  const separator = ["\t", ";", "|"].find((sep) => rows.some((row) => row.includes(sep)));
  if (!separator) return err("chartSeparator");
  const cells = rows.map((row) =>
    (separator === "|" ? row.replace(/^\|+|\|+$/g, "") : row)
      .split(separator)
      .map((cell) => cell.trim()),
  );
  const width = cells[0].length;
  if (width < 2) return err("chartColumns");
  if (cells.some((row) => row.length !== width)) return err("chartRowWidth");
  const categories = cells.length - 1;
  const series = width - 1;
  if (categories < 2 || categories > 12) return err("chartCategories", { count: categories });
  if (series > 5) return err("chartSeries", { count: series });
  for (let row = 1; row < cells.length; row += 1) {
    const bad = cells[row]
      .slice(1)
      .find((cell) => !NUMBER_CELL_PATTERN.test(cell.replace(/[%€$\s\u00a0\u202f]/g, "")));
    if (bad !== undefined) return err("chartNumber", { row: row + 1, cell: bad });
  }
  return null;
};

// Validation

const optionValues = (field: DocumentCraftingFormField) =>
  (field.options ?? []).map((option) => option.value);

/** Error of a single value (text, number, select, …) or null. `limit`
 * overrides the field's limits (`limits_by_count` of its list). */
const validateScalar = (
  field: DocumentCraftingFormField,
  value: unknown,
  required: boolean,
  limit?: DocumentCraftingCountLimit,
): FormError | null => {
  if (field.type === "boolean") {
    // A required checkbox must be ticked (e.g. "The client approved …").
    return required && value !== true ? err("mustBeTicked") : null;
  }
  if (field.type === "multiselect") {
    const items = Array.isArray(value) ? value : [];
    if (!items.length) return required ? err("selectRequired") : null;
    if (field.options?.length && items.some((item) => !optionValues(field).includes(String(item))))
      return err("option");
    if (field.min_items && items.length < field.min_items)
      return err("minSelected", { min: field.min_items });
    if (field.max_items && items.length > field.max_items)
      return err("maxSelected", { max: field.max_items });
    return null;
  }
  if (isEmptyValue(value)) {
    if (!required) return null;
    return err(field.type === "select" ? "selectRequired" : "required");
  }
  if (field.type === "number") {
    if (typeof value !== "number" || !Number.isFinite(value)) return err("number");
    if (!Number.isInteger(value)) return err("wholeNumber");
    if (field.min !== null && field.min !== undefined && value < field.min)
      return err("min", { min: field.min });
    if (field.max !== null && field.max !== undefined && value > field.max)
      return err("max", { max: field.max });
    return null;
  }
  if (field.type === "select") {
    const choice = String(value).trim();
    if (optionValues(field).includes(choice)) return null;
    if (!field.allow_custom) return err("option");
    if (field.max_length && choice.length > field.max_length)
      return err("maxLength", { max: field.max_length, length: choice.length });
    return null;
  }
  if (field.type === "date") return isValidDate(String(value)) ? null : err("date");
  if (field.type === "time") return isValidTime(String(value)) ? null : err("time");

  const text = normalizeText(field, String(value));
  const maxLength = limit?.max_length ?? field.max_length;
  const maxWords = limit?.max_words ?? field.max_words;
  if (field.min_length && text.length < field.min_length)
    return err("minLength", { min: field.min_length, length: text.length });
  if (maxLength && text.length > maxLength)
    return err("maxLength", { max: maxLength, length: text.length });
  if (maxWords && wordCount(text) > maxWords)
    return err("maxWords", { max: maxWords, count: wordCount(text) });
  if (field.type === "url" && !isValidUrl(text)) return err("url");
  if (field.type === "email" && !isValidEmail(text)) return err("email");
  if (field.format === "url_or_email" && !isUrlOrEmail(text)) return err("urlOrEmail");
  if (field.format === "phone" && !isValidPhone(text)) return err("phone");
  if (field.format === "chart_table") return chartTableProblem(text);
  return null;
};

/** The limits that apply to the rows of a list with `count` entries. */
export const rowLimits = (field: DocumentCraftingFormField, count: number): Record<string, DocumentCraftingCountLimit> =>
  field.limits_by_count?.[String(count)] ?? {};

/** The item fields of a list with `limits_by_count` applied. */
export const limitedItemFields = (field: DocumentCraftingFormField, count: number): DocumentCraftingFormField[] => {
  const limits = rowLimits(field, count);
  return (field.item_fields ?? []).map((item) =>
    limits[item.key]
      ? {
          ...item,
          max_length: limits[item.key].max_length ?? item.max_length,
          max_words: limits[item.key].max_words ?? item.max_words,
        }
      : item,
  );
};

const allowedCountsError = (allowed: number[], count: number): FormError =>
  allowed.length === 1
    ? err("exactItems", { count: allowed[0], given: count })
    : err("allowedCounts", {
        list: allowed.slice(0, -1).join(", "),
        last: allowed[allowed.length - 1],
        count,
      });

const validateList = (
  field: DocumentCraftingFormField,
  value: unknown,
  path: string,
  required: boolean,
  context: ValidationContext,
): FormErrors => {
  const items = Array.isArray(value) ? value : [];
  const rows = items.map((item, index) => ({ item, index, path: joinPath(path, index) }));
  const entries = rows.filter((row) => !isBlankRow(field, row.item, row.path, context.files));
  const count = entries.length;
  if (!count && !required) return {};

  const errors: FormErrors = {};
  const needed = count ? (field.min_items ?? 0) : Math.max(1, field.min_items ?? 1);
  if (!count) {
    errors[path] = needed > 1 ? err("minItems", { min: needed, count }) : err("listRequired");
  } else if (field.min_items && count < field.min_items) {
    errors[path] = err("minItems", { min: field.min_items, count });
  } else if (field.max_items && count > field.max_items) {
    errors[path] = err("maxItems", { max: field.max_items, count });
  } else if (field.allowed_counts?.length && !field.allowed_counts.includes(count)) {
    errors[path] = allowedCountsError(field.allowed_counts, count);
  }

  // Rows to check: the entries, plus as many empty rows as are missing for
  // the minimum, so their required fields show what is still to fill in.
  const blanks = rows.filter((row) => !entries.includes(row)).slice(0, Math.max(0, needed - count));
  const checked = rows.filter((row) => entries.includes(row) || blanks.includes(row));
  const limits = rowLimits(field, count);
  checked.forEach((row) => {
    if (field.item_fields?.length) {
      Object.assign(
        errors,
        validateFields(field.item_fields, context, row.path, asRecord(row.item), limits),
      );
    } else {
      // A list of plain texts: every row needs a text within the limits.
      const rowError = validateScalar({ ...field, type: "text" }, row.item, true);
      if (rowError) errors[row.path] = rowError;
    }
  });

  (field.item_fields ?? [])
    .filter((item) => item.exclusive)
    .forEach((item) => {
      entries
        .filter((row) => asRecord(row.item)[item.key] === true)
        .slice(1)
        .forEach((row) => {
          errors[joinPath(row.path, item.key)] = err("exclusive");
        });
    });
  return errors;
};

const validateFiles = (
  field: DocumentCraftingFormField,
  value: unknown,
  path: string,
  required: boolean,
  context: ValidationContext,
): FormErrors => {
  const ids = Array.isArray(value) ? value : [];
  const pending = context.files[path] ?? [];
  const count = ids.length + pending.length;
  if (!count) return required ? { [path]: err("fileRequired") } : {};
  if (field.max_files && count > field.max_files)
    return { [path]: err("maxFiles", { max: field.max_files }) };
  const min = field.min_width_px;
  if (min) {
    // Images below the minimum width are rejected by the server.
    const small = [
      ...(context.uploads?.[path] ?? [])
        .filter((upload) => ids.includes(upload.id))
        .map((upload) => ({ name: upload.filename, width: upload.image_meta?.width_px })),
      ...pending.map((item) => ({ name: item.file.name, width: item.image?.width_px })),
    ].find((item) => typeof item.width === "number" && item.width < min);
    if (small) return { [path]: err("imageTooSmall", { name: small.name, width: small.width, min }) };
  }
  return {};
};

const validateField = (
  field: DocumentCraftingFormField,
  context: ValidationContext,
  prefix: string,
  scope: Record<string, unknown>,
  limit?: DocumentCraftingCountLimit,
): FormErrors => {
  const path = joinPath(prefix, field.key);
  const value = scope[field.key];
  const required = isFieldRequired(field, scope, context);

  if (field.type === "group") {
    // Subfields count as required only while the group is active: it is
    // required, its condition holds, or something was entered in it.
    if (!required && isBlankField(field, value, path, context.files)) return {};
    return validateFields(field.fields ?? [], context, path, asRecord(value));
  }
  if (field.type === "list") return validateList(field, value, path, required, context);
  if (field.type === "file") return validateFiles(field, value, path, required, context);

  // Like the server, a missing value falls back to the field's default
  // (e.g. price conditions "net").
  const effective =
    (value === undefined || value === null) && field.default !== undefined && field.default !== null
      ? field.default
      : value;
  const scalarError = validateScalar(field, effective, required, limit);
  return scalarError ? { [path]: scalarError } : {};
};

/** Errors of `fields` (and everything nested in them), keyed by path.
 * `limits` are row limits of the list the fields belong to. */
export const validateFields = (
  fields: DocumentCraftingFormField[],
  context: ValidationContext,
  prefix = "",
  scope: Record<string, unknown> = context.root,
  limits: Record<string, DocumentCraftingCountLimit> = {},
): FormErrors =>
  fields.reduce<FormErrors>(
    (errors, field) => ({
      ...errors,
      ...validateField(field, context, prefix, scope, limits[field.key]),
    }),
    {},
  );

/** Cross-field rules of single types (the server's `_TYPE_RULES`). */
export const typeRuleErrors = (typeKey: string, values: FormValues): FormErrors => {
  const errors: FormErrors = {};
  if (typeKey === "invitation") {
    const start = typeof values.time_from === "string" ? values.time_from : "";
    const end = typeof values.time_to === "string" ? values.time_to : "";
    const pad = (time: string) => (time.length === 4 ? `0${time}` : time);
    if (isValidTime(start) && isValidTime(end) && pad(end) <= pad(start)) {
      errors.time_to = err("endBeforeStart");
    }
    const date = typeof values.date === "string" ? values.date : "";
    const deadline =
      typeof values.registration_deadline === "string" ? values.registration_deadline : "";
    if (isValidDate(date) && isValidDate(deadline) && deadline > date) {
      errors.registration_deadline = err("deadlineAfterEvent");
    }
  }
  return errors;
};

/** Errors of plain inputs outside the schema (change request, duplicate note). */
export const validateText = (
  value: string,
  options: { required?: boolean; minLength?: number; maxLength?: number },
): FormError | null =>
  validateScalar(
    {
      key: "",
      type: "textarea",
      step: "basics",
      required: !!options.required,
      label: { en: "", de: "" },
      min_length: options.minLength ?? null,
      max_length: options.maxLength ?? null,
    },
    value,
    !!options.required,
  );

export const countPendingFiles = (files: PendingFiles) =>
  Object.keys(files).reduce((total, key) => total + files[key].length, 0);

// Steps

export const fieldStep = (field: DocumentCraftingFormField): FieldStep =>
  field.step === "basics" || field.step === "files" ? field.step : "details";

export const fieldsOfStep = (fields: DocumentCraftingFormField[], step: FieldStep) =>
  fields.filter((field) => fieldStep(field) === step);

/** The wizard step of an error path; null when no field matches. */
export const stepOfPath = (path: string, fields: DocumentCraftingFormField[]): FieldStep | null => {
  const top = segments(path)[0];
  const field = fields.find((item) => item.key === top);
  return field ? fieldStep(field) : null;
};

/** Whether `path` points at a field of the schema (or into one). */
export const isKnownPath = (path: string, fields: DocumentCraftingFormField[]): boolean => {
  const [head, ...rest] = segments(path);
  const field = fields.find((item) => item.key === head);
  if (!field) return false;
  if (!rest.length) return true;
  if (field.type === "group") return isKnownPath(rest.join("."), field.fields ?? []);
  if (field.type === "list") {
    const [index, ...inner] = rest;
    if (!/^\d+$/.test(index)) return false;
    return !inner.length || isKnownPath(inner.join("."), field.item_fields ?? []);
  }
  // One file of an upload field ("photos.0").
  if (field.type === "file") return rest.length === 1 && /^\d+$/.test(rest[0]);
  return false;
};

/** The upload field that owns a per-file path ("photos.0" → "photos",
 * "packages.1.logo.0" → "packages.1.logo"); other paths stay as they are.
 * The registry reports file problems per file, but the form shows them on
 * the upload field. */
export const fileFieldPath = (path: string, fields: DocumentCraftingFormField[]): string => {
  const parts = segments(path);
  if (parts.length < 2 || !/^\d+$/.test(parts[parts.length - 1])) return path;
  const owner = parts.slice(0, -1);
  const field = fieldAtPath(
    fields,
    owner.filter((part) => !/^\d+$/.test(part)).join("."),
  );
  return field?.type === "file" ? owner.join(".") : path;
};

// Server errors (400 `invalid_form`, `errors: {path: message}`)

/** "values.packages[0].name", "/values/packages/0/name" and
 * "packages.0.name" all become "packages.0.name". */
export const normalizeErrorPath = (path: string) =>
  path
    .trim()
    .replace(/\[(\d+)\]/g, ".$1")
    .replace(/\//g, ".")
    .replace(/~1/g, "/")
    .replace(/~0/g, "~")
    .replace(/\.{2,}/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .replace(/^values\./, "");

/** Errors per normalized path; with the schema, per-file errors are folded
 * onto their upload field (several joined with "; "). */
export const mapServerErrors = (
  errors: Record<string, unknown> | null | undefined,
  fields: DocumentCraftingFormField[] = [],
): FormErrors => {
  const mapped: FormErrors = {};
  Object.keys(errors ?? {}).forEach((path) => {
    const value = (errors as Record<string, unknown>)[path];
    const message = typeof value === "string" ? value : String(value);
    const target = fileFieldPath(normalizeErrorPath(path), fields);
    const previous = mapped[target]?.message;
    mapped[target] = { message: previous ? `${previous}; ${message}` : message };
  });
  return mapped;
};

// Initial values and prefills

export interface PrefillSources {
  user?: { name: string; surname: string; email: string } | null;
  settings?: { contact_phone?: string | null; contact_role?: string | null } | null;
  workspace?: { website_url?: string | null } | null;
  /** YYYY-MM-DD, for `prefill: "today"`. */
  today?: string;
}

export const todayIso = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

export const prefillValue = (
  prefill: DocumentCraftingPrefill | undefined,
  sources: PrefillSources,
): string | null => {
  switch (prefill) {
    case "user.full_name":
      return sources.user
        ? `${sources.user.name ?? ""} ${sources.user.surname ?? ""}`.trim() || null
        : null;
    case "user.email":
      return sources.user?.email || null;
    case "settings.contact_phone":
      return sources.settings?.contact_phone || null;
    case "settings.contact_role":
      return sources.settings?.contact_role || null;
    case "workspace.website_url":
      return sources.workspace?.website_url || null;
    case "today":
      return sources.today ?? null;
    default:
      return null;
  }
};

const clone = <T>(value: T): T =>
  value === null || value === undefined ? value : JSON.parse(JSON.stringify(value));

/** The value of a field nobody has entered anything in. */
export const emptyValue = (field: DocumentCraftingFormField): unknown => {
  switch (field.type) {
    case "number":
    case "select":
      return null;
    case "multiselect":
    case "file":
      return [];
    case "boolean":
      return false;
    case "group":
      return buildInitialValues(field.fields ?? [], null);
    case "list":
      return [];
    default:
      return "";
  }
};

/** A new row of a list (all item fields empty or default). */
export const emptyListItem = (field: DocumentCraftingFormField): unknown =>
  field.item_fields?.length ? buildInitialValues(field.item_fields, null) : "";

const initialFieldValue = (
  field: DocumentCraftingFormField,
  sources: PrefillSources | null,
  base: Record<string, unknown> | null,
): unknown => {
  const hasBase = !!base && field.key in base && base[field.key] !== undefined;
  const baseValue = hasBase ? (base as Record<string, unknown>)[field.key] : undefined;

  if (field.type === "group") {
    return buildInitialValues(
      field.fields ?? [],
      sources,
      hasBase ? asRecord(baseValue) : null,
    );
  }
  if (field.type === "list" && hasBase) {
    const items = Array.isArray(baseValue) ? baseValue : [];
    return items.map((item) =>
      field.item_fields?.length
        ? buildInitialValues(field.item_fields, null, asRecord(item))
        : String(item ?? ""),
    );
  }
  if (hasBase) return clone(baseValue);

  const prefill = sources ? prefillValue(field.prefill, sources) : null;
  if (prefill !== null) return prefill;
  if (field.default !== null && field.default !== undefined) return clone(field.default);
  if (field.type === "list" && field.required && !field.ask_if_missing) {
    // Required lists start with their minimum number of rows.
    const rows = Math.max(1, field.min_items ?? 1);
    return Array.from({ length: rows }, () => emptyListItem(field));
  }
  return emptyValue(field);
};

/**
 * Initial form values: the base values where present (duplicate, revise),
 * else the prefill (user, settings, workspace, today), else the field's
 * default, else empty. Keys of the base that the schema does not know are
 * kept, so nothing is lost on a round trip.
 */
export const buildInitialValues = (
  fields: DocumentCraftingFormField[],
  sources: PrefillSources | null,
  base: Record<string, unknown> | null = null,
): FormValues => {
  const values: FormValues = base ? clone(base) : {};
  fields.forEach((field) => {
    values[field.key] = initialFieldValue(field, sources, base);
  });
  return values;
};

/** Values as sent to the server: texts trimmed. */
export const trimValues = <T>(value: T): T => {
  if (typeof value === "string") return value.trim() as unknown as T;
  if (Array.isArray(value)) return value.map(trimValues) as unknown as T;
  if (isRecord(value)) {
    const next: Record<string, unknown> = {};
    Object.keys(value).forEach((key) => {
      next[key] = trimValues(value[key]);
    });
    return next as T;
  }
  return value;
};

// File fields and earlier uploads (duplicate, revise)

export interface FileFieldRef {
  path: string;
  field: DocumentCraftingFormField;
}

/** File fields at the top level and inside groups (lists carry no files). */
export const collectFileFields = (fields: DocumentCraftingFormField[], prefix = ""): FileFieldRef[] =>
  fields.reduce<FileFieldRef[]>((refs, field) => {
    const path = joinPath(prefix, field.key);
    if (field.type === "file") return [...refs, { path, field }];
    if (field.type === "group") return [...refs, ...collectFileFields(field.fields ?? [], path)];
    return refs;
  }, []);

const UPLOAD_KIND_BY_FILE_KIND: Record<DocumentCraftingFileKind, DocumentCraftingUploadKind> = {
  source: DocumentCraftingUploadKind.SOURCE,
  existing_material: DocumentCraftingUploadKind.EXISTING_MATERIAL,
  partner_logo: DocumentCraftingUploadKind.PARTNER_LOGO,
  photo: DocumentCraftingUploadKind.PHOTO,
  logo: DocumentCraftingUploadKind.LOGO,
  portrait: DocumentCraftingUploadKind.PORTRAIT,
};

/** The file field an earlier upload belongs to: its `field_key`, else the
 * first field of its kind; null when none fits. */
export const uploadFieldPath = (
  upload: DocumentCraftingUpload,
  fileFields: FileFieldRef[],
): string | null => {
  if (upload.field_key) {
    const key = normalizeErrorPath(upload.field_key);
    const byKey = fileFields.find((ref) => ref.path === key || ref.field.key === key);
    if (byKey) return byKey.path;
  }
  const byKind = fileFields.find(
    (ref) => ref.field.file_kind && UPLOAD_KIND_BY_FILE_KIND[ref.field.file_kind] === upload.kind,
  );
  return byKind ? byKind.path : null;
};

/** Earlier uploads that belong to the form (not the native file of a manual upload). */
export const formUploads = (uploads: DocumentCraftingUpload[]) =>
  uploads.filter((upload) => upload.kind !== DocumentCraftingUploadKind.MANUAL_REVISION);

/**
 * Which earlier uploads start ticked, per file field path, plus the ticked
 * ones no field fits ("other"). `activeIds` = the uploads used last time
 * (`inputs.upload_ids`); null or empty ticks all of them.
 */
export const initialUploadSelection = (
  fileFields: FileFieldRef[],
  uploads: DocumentCraftingUpload[],
  activeIds: number[] | null,
): { byPath: Record<string, number[]>; other: number[] } => {
  const ticked = (upload: DocumentCraftingUpload) =>
    !activeIds || !activeIds.length || activeIds.includes(upload.id);
  const byPath: Record<string, number[]> = {};
  fileFields.forEach((ref) => {
    byPath[ref.path] = [];
  });
  const other: number[] = [];
  formUploads(uploads).forEach((upload) => {
    if (!ticked(upload)) return;
    const path = uploadFieldPath(upload, fileFields);
    if (path) byPath[path].push(upload.id);
    else other.push(upload.id);
  });
  return { byPath, other };
};

/** Writes the ticked upload ids into the file fields of `values`. */
export const withUploadSelection = (
  values: FormValues,
  byPath: Record<string, number[]>,
): FormValues =>
  Object.keys(byPath).reduce((next, path) => setAt(next, path, byPath[path]), values);

/** Upload ids currently ticked in the file fields of `values`. */
export const selectedUploadIds = (values: FormValues, fileFields: FileFieldRef[]) =>
  fileFields.reduce<number[]>((ids, ref) => {
    const value = getAt(values, ref.path);
    return Array.isArray(value)
      ? [...ids, ...value.filter((id): id is number => typeof id === "number")]
      : ids;
  }, []);

/** New files for the request, keyed by the field path (`files[<field_key>]`). */
/** File kinds that are pictures in the document and carry an alt text. */
export const PICTURE_FILE_KINDS = ["photo", "logo", "portrait", "partner_logo"];
export const ALT_MIN_LENGTH = 3;
export const ALT_MAX_LENGTH = 200;

/** Error key of an alt text (`create.upload.altInvalid`), or null when fine/empty. */
export const altProblem = (alt: string | undefined): string | null => {
  const text = (alt ?? "").trim().replace(/\s+/g, " ");
  if (!text) return null;
  if (text.length < ALT_MIN_LENGTH || text.length > ALT_MAX_LENGTH || /\[[^\]]*\]/.test(text)) {
    return "altInvalid";
  }
  return null;
};

/** `upload_alts` of a request: per field, the alt of each new file in order;
 * only fields with at least one alt text. */
export const altsForRequest = (files: PendingFiles): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  Object.keys(files).forEach((path) => {
    const alts = files[path].map((item) => (item.alt ?? "").trim());
    if (alts.some(Boolean)) result[path] = alts;
  });
  return result;
};

export const filesForRequest = (files: PendingFiles): Record<string, File[]> => {
  const result: Record<string, File[]> = {};
  Object.keys(files).forEach((path) => {
    if (files[path].length) result[path] = files[path].map((item) => item.file);
  });
  return result;
};

/** Earlier uploads per file field path (uploads no field fits are left out). */
export const groupUploadsByPath = (
  uploads: DocumentCraftingUpload[],
  fileFields: FileFieldRef[],
): Record<string, DocumentCraftingUpload[]> => {
  const byPath: Record<string, DocumentCraftingUpload[]> = {};
  formUploads(uploads).forEach((upload) => {
    const path = uploadFieldPath(upload, fileFields);
    if (path) byPath[path] = [...(byPath[path] ?? []), upload];
  });
  return byPath;
};
